// Authentication API Types

/**
 * 직무 Enum
 */
export enum JobTitle {
	DOCTOR = 'DOCTOR',           // 의사
	NURSE = 'NURSE',             // 간호사
	NURSING_AIDE = 'NURSING_AIDE', // 간호조무사
	PARAMEDIC = 'PARAMEDIC',     // 응급구조사
	EDUCATOR = 'EDUCATOR',       // 교육자
	STUDENT = 'STUDENT',         // 학생
	RESEARCHER = 'RESEARCHER',   // 연구원
	CUSTOM = 'CUSTOM'            // 직접입력
}

/**
 * POST /user/login
 * 로그인 (액세스/리프레시 토큰 발급)
 */
export interface LoginDto {
	loginId: string;
	password: string;
}

export interface LoginResponseDto {
	accessToken: string;
	refreshToken: string;
}

/**
 * POST /user/signup
 * 회원가입
 */
export interface SignupRequestDto {
	loginId: string;
	pw: string; // 백엔드 API 스펙: pw 사용
	realName: string;
	/** 브라우저 언어 — 백엔드 enum: 'ko' | 'en', 필수 */
	language: 'ko' | 'en';
	email?: string;
	phoneNumber?: string;
	affiliation?: string;
	department?: string;
	position?: string;
	profileImageUrl?: string;
	termsIdList: number[];
}

export interface SignupResponseDto {
	userId: number;
}

/**
 * POST /auth/access-token
 * 리프레시 토큰으로 액세스 토큰 재발급
 */
export interface RefreshTokenRequestDto {
	refreshToken: string;
}

/** OpenAPI: CreateAccessTokenByRefreshTokenResponseDto — accessToken만 반환 */
export interface RefreshTokenResponseDto {
	accessToken: string;
}

/**
 * POST /auth/send-verification-email
 * 인증 메일 발송
 */
export interface SendVerificationEmailRequestDto {
	email: string;
}

export interface SendVerificationEmailResponseDto {
	message: string;
}

/**
 * POST /auth/verify-verification-code
 * 이메일 인증 코드 검증
 */
export interface VerifyVerificationCodeRequestDto {
	email: string;
	verificationCode: string; // 기존 코드와 호환
}

export interface VerifyVerificationCodeResponseDto {
	message: string;
	isValid: boolean;
}

/**
 * POST /auth/password-reset
 * 비밀번호 재설정 요청
 */
export interface PasswordResetRequestDto {
	email: string;
}

export interface PasswordResetResponseDto {
	message: string;
	resetToken: string;
}

/**
 * POST /user/change-password
 * Swagger `ChangePasswordRequestDto` 와 동일 필드.
 */
export interface ChangePasswordRequestDto {
	/** 리셋 토큰 (이메일 링크). 예: `123e4567-e89b-12d3-a456-426614174000` */
	resetToken: string;
	/** 새 비밀번호. 서버 검증: minLength 8 */
	newPassword: string;
}

export interface ChangePasswordResponseDto {
	message: string;
}

/**
 * PATCH /user/my-password
 * 내 비밀번호 변경 (본인)
 */
export interface ChangeMyPasswordRequestDto {
	currentPassword: string;   // 필수, 현재 비밀번호
	newPassword: string;       // 필수, 새 비밀번호 (최소 8자)
}

export interface ChangeMyPasswordResponseDto {
	message: string;
}

/**
 * POST /user/change-password-admin
 * 비밀번호 변경 (관리자)
 */
export interface ChangePasswordAdminRequestDto {
	userId: number;
	newPassword: string;
}

export interface ChangePasswordAdminResponseDto {
	message: string;
}

/**
 * POST /auth/send-deletion-code
 * 계정 삭제 인증 코드 메일 발송
 */
export interface SendDeletionCodeRequestDto {
	email: string;
}

export interface SendDeletionCodeResponseDto {
	message: string;
}

/**
 * POST /auth/verify-deletion-code
 * 계정 삭제 인증 코드 검증
 */
export interface VerifyDeletionCodeRequestDto {
	email?: string;
	deletionCode: string; // 기존 코드와 호환
}

export interface VerifyDeletionCodeResponseDto {
	message: string;
	isValid?: boolean;
	deletionToken?: string;
}

/**
 * DELETE /user/my-account
 * 회원 탈퇴
 */
export interface DeleteMyAccountRequestDto {
	feedback?: string;
}

export interface DeleteMyAccountResponseDto {
	message: string;
}

/**
 * POST /user/check-email
 * 이메일 중복 확인
 */
export interface CheckEmailRequestDto {
	email: string;
}

export interface CheckEmailResponseDto {
	isAvailable: boolean;
	message?: string;
}

/**
 * POST /user/check-login-id
 * 로그인 ID 중복 확인
 */
export interface CheckLoginIdRequestDto {
	loginId: string;
}

export interface CheckLoginIdResponseDto {
	isAvailable: boolean;
	message?: string;
}

/**
 * GET /user/profile
 * 유저 프로필 조회
 */
export interface GetUserProfileResponseDto {
	userId: number;
	loginId?: string;
	realName: string;
	email: string | null;
	/** 개인 프로필 국가 ID */
	countryId?: number | null;
	/** 개인 프로필 국가명 */
	countryName?: string | null;
	/** OpenAPI: 소속 (nullable) */
	affiliation?: string | null;
	/** OpenAPI UserProfileResponseDto.language — 예: ko, en */
	language?: string | null;
	phoneNumber?: string | null;
	department?: string; // 레거시 필드 (호환용)
	position?: string;
	/** 직무 enum */
	jobTitle?: JobTitle | null;
	/** 직무 직접입력 */
	customJobTitle?: string | null;
	role: number; // 0: guest, 1: student, 2: master, 4: sales_admin, 5: admin
	organizationId?: number | null;
	organizationName?: string | null;
	/** OpenAPI: 기관 로고 이미지 URL */
	organizationLogoImageUrl?: string | null;
	profileImageUrl?: string | null;
	createdAt: string;
	lastSigninAt?: string;
	signinCount?: number;
	guestAccountExpiresAt?: string | null;
}

/**
 * PATCH /user/profile
 * 유저 프로필 변경
 */
export interface UpdateUserProfileRequestDto {
	realName?: string;
	/** OpenAPI 프로필 응답과 동일 키 — 일부 서버는 PATCH 시 필수 */
	language?: string;
	/** 생략: 기존값 유지, number: 국가 변경, null: 국가 제거 */
	countryId?: number | null;
	phoneNumber?: string;
	department?: string; // 레거시 필드 (호환용)
	position?: string;
	/** 직무 enum */
	jobTitle?: JobTitle;
	/** 직무 직접입력 (jobTitle이 CUSTOM일 때) */
	customJobTitle?: string;
	/** OpenAPI: 소속 */
	affiliation?: string;
	profileImageUrl?: string;
}

export interface UpdateUserProfileResponseDto {
	message: string;
}
